// frontend/src/modules/invoicing/pages/ServiceEditorPage.tsx
import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { LoadingState } from '../../../design-system/components/LoadingState';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { useApi } from '../../../design-system/useApi';
import { VISUAL } from '../../../design-system/visual';
import { servicesApi } from '../api';
import type { Service, CreateServiceInput } from '../types';

const EMPTY = {
  name: '', description: '', default_unit_price: 0, default_tax_rate: 0, unit: 'each',
};

export function ServiceEditorPage() {
  const { id } = useParams<{ id: string }>();
  const mode = id ? 'edit' : 'new';
  const navigate = useNavigate();

  const { data, loading } = useApi<{ service: Service }>(mode === 'edit' && id ? `/api/invoicing/services/${id}` : null);
  const [form, setForm] = React.useState<CreateServiceInput & { is_active?: boolean }>(EMPTY);
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (data?.service) {
      const s = data.service;
      setForm({
        name: s.name,
        description: s.description ?? '',
        default_unit_price: Number(s.default_unit_price),
        default_tax_rate: Number(s.default_tax_rate),
        unit: s.unit,
        is_active: s.is_active,
      });
    }
  }, [data]);

  async function save(e: React.FormEvent) {
    e.preventDefault();
    if (!form.name?.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      if (mode === 'edit' && id) {
        await servicesApi.update(id, form);
        toast.success('Service updated');
      } else {
        await servicesApi.create(form);
        toast.success('Service created');
      }
      navigate('/dashboard/services');
    } catch (err: any) {
      toast.error('Save failed', { description: err.message });
    } finally {
      setSaving(false);
    }
  }

  if (mode === 'edit' && loading) return <LoadingState variant="page" />;

  return (
    <form onSubmit={save}>
      <PageHeader
        eyebrow={<><Link to="/dashboard/services" className="hover:text-foreground transition-colors">Services</Link> / {mode === 'edit' ? data?.service?.name : 'New'}</>}
        title={mode === 'edit' ? `Edit ${data?.service?.name ?? 'service'}` : 'New service'}
        description={mode === 'edit' ? 'Adjust defaults. Existing invoice line items keep their original values.' : 'Define a service once, drop it onto invoices in one click.'}
        actions={
          <div className="flex items-center gap-2">
            <Link to="/dashboard/services">
              <Button variant="outline" type="button" className="rounded-full h-10 px-4 text-[13px] font-semibold">Cancel</Button>
            </Link>
            <Button type="submit" disabled={saving} className="rounded-full h-10 px-5 text-[13px] font-semibold">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 max-w-3xl space-y-6">
        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Basics</h3>
          <div className="grid gap-5">
            <div className="grid gap-1.5">
              <Label>Name<span className="text-destructive ml-0.5">*</span></Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required placeholder="Consulting hour" />
            </div>
            <div className="grid gap-1.5">
              <Label>Description</Label>
              <Textarea rows={2} value={form.description ?? ''} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Optional — appears as the default invoice line description." />
            </div>
          </div>
        </section>

        <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
          <h3 className={[VISUAL.dashSection, 'mb-5'].join(' ')}>Pricing defaults</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="grid gap-1.5">
              <Label>Default price</Label>
              <Input type="number" step="0.01" min="0" value={form.default_unit_price ?? 0} onChange={(e) => setForm({ ...form, default_unit_price: Number(e.target.value) })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Default tax %</Label>
              <Input type="number" step="0.01" min="0" max="100" value={form.default_tax_rate ?? 0} onChange={(e) => setForm({ ...form, default_tax_rate: Number(e.target.value) })} />
            </div>
            <div className="grid gap-1.5">
              <Label>Unit</Label>
              <Input value={form.unit ?? 'each'} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="hour, day, each" />
            </div>
          </div>
        </section>

        {mode === 'edit' && (
          <section className={[VISUAL.bento, 'p-6 sm:p-7'].join(' ')}>
            <h3 className={[VISUAL.dashSection, 'mb-2'].join(' ')}>Availability</h3>
            <label className="flex items-start gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active ?? true}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                className="mt-1 h-4 w-4 rounded border-border accent-saffron"
              />
              <span className="flex-1">
                <span className="text-[13.5px] font-semibold text-foreground block">Available in the invoice editor</span>
                <span className="text-[12.5px] text-muted-foreground block mt-0.5">Uncheck to keep the service in the catalog but hide it from the line-item picker.</span>
              </span>
            </label>
          </section>
        )}

        <div className="flex items-center justify-between pt-2">
          <Link to="/dashboard/services" className="text-[13px] font-semibold text-muted-foreground hover:text-foreground transition-colors">
            Back without saving
          </Link>
          <Button type="submit" disabled={saving} className="rounded-full h-11 px-6 text-[14px] font-semibold">
            {saving ? 'Saving…' : (mode === 'edit' ? 'Save changes' : 'Create service')}
          </Button>
        </div>
      </div>
    </form>
  );
}
